import { Badge, Box, ListItemIcon, ListItemButton, ListItemText, Typography } from '@mui/material';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MetaData, PushTriggerOption } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { UserMessage } from '@sendbird/chat/message';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { currentContextAtom, userAtom } from '../state';
import { useCompanyChannelInfoByIdLazyQuery } from '../queries/__generated__/companyChannelInfoById.generated';
import Utils from '../common/utils/common.utils';
import { ellipsisStyle } from '../common/utils/style.utils';
import { currentChannelAtom } from './state';
import ChannelCompanyImage from './ChannelCompanyImage';

export interface ChannelListPreviewProps {
  channel: GroupChannel;
  onSelectChannel: () => void;
}

export default function ChannelListPreview({ channel, onSelectChannel }: ChannelListPreviewProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'chat']);
  const { id: userId } = useAtomValue(userAtom);
  const { realm } = useAtomValue(currentContextAtom);
  const [companyChannelInfoById, { data: companyChannelInfoData }] = useCompanyChannelInfoByIdLazyQuery();
  const [channelMetadata, setChannelMetadata] = useState<MetaData | undefined>();

  const currentChannel = useAtomValue(currentChannelAtom);
  const shouldSeeDot = channel.unreadMessageCount;

  useEffect((): void => {
    const getCurrentChannelMetadata = async (): Promise<void> => {
      const metadata = await channel.getAllMetaData();

      setChannelMetadata(metadata);
    };

    if (!Object.keys(channel.cachedMetaData).length) {
      getCurrentChannelMetadata();
    } else {
      setChannelMetadata(channel.cachedMetaData as MetaData);
    }
  }, []);

  useEffect((): void => {
    if (channelMetadata) {
      companyChannelInfoById({
        variables: {
          companyId: realm === Business_Profile_Enum.Buyer
            ? (channel.cachedMetaData as MetaData).supplyingCompanyId
            : (channel.cachedMetaData as MetaData).buyingCompanyId,
        },
      });
    }
  }, [channelMetadata]);

  return (
    <ListItemButton
      onClick={() => onSelectChannel()}
      selected={channel.url === currentChannel?.url}
    >
      {companyChannelInfoData?.company_by_pk && (
        <>
          <ListItemIcon>
            <ChannelCompanyImage
              company={{
                businessProfile: companyChannelInfoData.company_by_pk.business_profile,
                photo: companyChannelInfoData.company_by_pk.photos[0]?.photo,
                brand: companyChannelInfoData.company_by_pk.brand,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={companyChannelInfoData.company_by_pk.tradeName}
            secondary={channel.lastMessage && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '.MuiTypography-root': channel.unreadMessageCount
                    ? { color: 'text.primary' }
                    : undefined,
                }}
              >
                <Typography
                  variant="labelSmall"
                  color="secondary"
                  sx={{
                    flex: 1,
                    mr: 0.5,
                    ...ellipsisStyle,
                  }}
                >
                  {/* Cast as UserMessage because sendbird definition uses BaseMessage instead even though it is actually a UserMessage */}
                  {(channel.lastMessage as UserMessage).sender.userId === userId && t('chat:you')}
                  {(channel.lastMessage as UserMessage).message}
                </Typography>
              </Box>
            )}
          />
          {channel.lastMessage && (
            <Typography variant="labelSmall" color="secondary" sx={{ flexShrink: 0 }}>
              {Utils.formatTimeAgo(new Date(channel.lastMessage.createdAt), { style: 'short' })}
            </Typography>
          )}
          {channel.myPushTriggerOption === PushTriggerOption.OFF && (
            <NotificationsOffOutlinedIcon sx={{ width: 18, height: 18, color: 'text.secondary' }} />
          )}
          {channel.myPushTriggerOption !== PushTriggerOption.OFF && (
            <Badge color="error" variant="dot" invisible={!shouldSeeDot} />
          )}
        </>
      )}
    </ListItemButton>
  );
}
